/**
 * Pre-baked toolbox-talk templates for UK construction.
 *
 * Each template populates topic / why / points / duration on the TBT form.
 * Users can edit any field before saving. To add more, append to the array.
 */

export interface ToolboxTalkTemplate {
  id: string;
  topic: string;
  why: string;
  points: string;
  duration_mins: number;
}

export const POPULAR_TBT_TEMPLATES: ToolboxTalkTemplate[] = [
  {
    id: 'manual_handling',
    topic: 'Manual handling',
    why: "Back injuries are the most common cause of lost work days in construction. We're moving heavy materials this week — refresh the basics before anyone goes off with a bad back.",
    points: [
      'Plan the lift — route, weight, drop point, two-person if >25kg.',
      'Get a stable stance, feet shoulder-width apart, close to the load.',
      'Bend the knees, keep the back straight; lift smoothly, no jerks.',
      'Don\'t twist the spine — turn with the feet.',
      'Mechanical aids (sack truck, telehandler, two-man) where available.',
    ].join('\n'),
    duration_mins: 10,
  },
  {
    id: 'working_at_height',
    topic: 'Working at height',
    why: 'Falls from height remain the single biggest cause of fatalities in UK construction. Anyone going up scaffold, MEWP or ladder needs to know the rules.',
    points: [
      'Avoid working at height where you can — bring the work to ground level.',
      'Scaffold tag must be in date and signed off this week. Toe-boards, guard-rails and mid-rails all in place.',
      'Ladders only for short-duration light work, footed and tied at the top.',
      'MEWPs — daily check, harness on operator, never overreach the basket.',
      'No-one works at height alone in a quiet area without check-ins.',
    ].join('\n'),
    duration_mins: 12,
  },
  {
    id: 'silica_dust',
    topic: 'Silica dust — wet cutting',
    why: 'Cutting concrete, brick, stone or mortar releases respirable crystalline silica. Long-term exposure causes silicosis and lung cancer; HSE are actively enforcing.',
    points: [
      'Always use water suppression OR on-tool dust extraction (M-class min).',
      'FFP3 mask, fit-tested, every time you cut — even briefly.',
      'Don\'t dry-sweep dust — vac with HEPA or damp down before clearing.',
      'Keep neighbours upwind clear of the cutting area.',
      'Change overalls / wash before lunch — no taking dust into the welfare cabin.',
    ].join('\n'),
    duration_mins: 10,
  },
  {
    id: 'havs',
    topic: 'Hand-arm vibration (HAVS)',
    why: 'HAVS is a RIDDOR-reportable occupational disease — permanent nerve and circulation damage from grinders, breakers and SDS drills. Daily exposure adds up fast.',
    points: [
      'Use the lowest-vibration tool that does the job; check the trigger time on each tool.',
      'Anti-vibration gloves + keep hands warm — cold worsens symptoms.',
      'Take regular breaks; rotate tasks across the team.',
      "Report early signs: tingling, numbness, white fingers in the cold — don't wait until it's permanent.",
      'Daily exposure action value (EAV) — keep tracking; the sheet lives in the site cabin.',
    ].join('\n'),
    duration_mins: 8,
  },
  {
    id: 'asbestos_awareness',
    topic: 'Asbestos awareness',
    why: 'Refurbs and extensions on pre-2000 properties almost always involve asbestos. Disturbing it without a survey is illegal and a health crisis.',
    points: [
      'Never disturb suspect material — if in doubt, STOP and call the Director.',
      'Refurb / Demolition Survey must be on file before strip-out begins.',
      'Common ACMs: artex, asbestos cement (corrugated roofs, downpipes), floor tiles, pipe lagging, AIB ceiling tiles.',
      'Anyone uncovering an unexpected ACM: STOP, photograph, mark area, contact site manager.',
      'No removal without a licensed contractor for licensable work.',
    ].join('\n'),
    duration_mins: 10,
  },
  {
    id: 'electrical_safety',
    topic: 'Electrical safety on site',
    why: 'Damaged leads and missing RCDs cause serious shocks every year. New tools and trailing cables are arriving daily — keep on top of it.',
    points: [
      '110V centre-tapped earth on site wherever practical, especially for hand tools.',
      'RCD in line with every supply. Test it weekly with the test button.',
      'Inspect leads before each use — no taped joints, no exposed copper.',
      'Battery tools where reasonable — eliminate the lead.',
      'PAT-test stickers on company tools; check date before plugging in.',
    ].join('\n'),
    duration_mins: 8,
  },
  {
    id: 'slips_trips',
    topic: 'Slips, trips and housekeeping',
    why: 'Most common cause of minor injury on site. Costs us days, claims and HSE attention. End-of-day tidy is everyone\'s job, not the labourer\'s.',
    points: [
      'Clear walkways at the start, after lunch, and at clock-off. No "I\'ll do it later".',
      'Trailing leads taped down or routed overhead.',
      'Spills — sand or absorbent pad, then mop. Cordon off until dry.',
      'Materials stacked safely, not blocking fire exits or scaffold steps.',
      'Skip not overfilled — overhang is a strike hazard for plant.',
    ].join('\n'),
    duration_mins: 6,
  },
  {
    id: 'ppe_refresher',
    topic: 'PPE refresher',
    why: 'Baseline PPE is non-negotiable on this site. New people are joining; existing people get complacent. Quick refresh — what, when, why.',
    points: [
      'Hard hat: at all times in active areas. Replace if dropped from height or after impact.',
      'Safety boots, mid-cut min: inspected weekly for soles, eyelets, midsole punctures.',
      'Hi-vis: clean, intact, the right class for the task (Class 2 minimum on most groundworks).',
      'Gloves matched to task — cut-resistant for sharp materials, chemical-resistant for solvents.',
      'Eye protection for cutting, drilling, grinding, chasing, demolition. No exceptions.',
    ].join('\n'),
    duration_mins: 7,
  },
  {
    id: 'excavation_safety',
    topic: 'Excavation safety',
    why: 'Excavation collapses kill silently and quickly. Service strikes (gas, electric, water) cost lives and £100k+ in damages. We\'re digging foundations / drainage this week.',
    points: [
      'Permit-to-Dig issued and signed before any new excavation.',
      'CAT-scan + Genny + service drawings cross-checked. Hand-dig last 300mm to known services.',
      'Sides supported / battered back where >1.2m deep.',
      'Barriers + lighting + signage around all open excavations, especially overnight.',
      'Daily inspection by a competent person; weekly recorded inspection.',
    ].join('\n'),
    duration_mins: 10,
  },
  {
    id: 'plant_pedestrian',
    topic: 'Plant and pedestrian interaction',
    why: 'Telehandlers and excavators on a tight site — being struck by moving plant is one of the most dangerous moments. Banksman and eye contact rules are enforced.',
    points: [
      'No-one in the swing radius of an excavator. Marked area on site plan.',
      'Banksman in hi-vis directing every reverse manoeuvre and lift.',
      'Eye contact with the operator before you cross in front. If you can\'t see them, they can\'t see you.',
      'Reverse alarm and beacon working — operator does the daily check.',
      'Pedestrian routes physically separated from plant routes where practical.',
    ].join('\n'),
    duration_mins: 8,
  },
  {
    id: 'hot_works',
    topic: 'Hot works and fire safety',
    why: 'Welding, grinding, soldering, cutting — all generate sparks that can smoulder for hours. Hot works permit + fire watch is mandatory, not optional.',
    points: [
      'Hot Works Permit issued before each shift involving sparks/heat.',
      'Fire watch person stays for 60 minutes after work stops. Set a timer.',
      "Extinguisher (CO2 + foam) on stand-by within 5 metres. Don't borrow it from the cabin.",
      'Combustibles cleared 10m radius, or shielded with fire blanket.',
      'No hot works in the last hour of the day — too easy to walk off and miss a smoulder.',
    ].join('\n'),
    duration_mins: 8,
  },
  {
    id: 'coshh',
    topic: 'COSHH on site',
    why: 'Cement, foam, paints, solvents, sealants — any product with a hazard label needs a COSHH assessment and the right controls. New products on site this week need adding to the register.',
    points: [
      'Read the SDS before opening a new product. Decant only what you need.',
      'PPE per the SDS — gloves and eye protection for almost everything.',
      'Ventilate when using solvents indoors. No grinding/sanding cured product without RPE.',
      'Cement burns: long-term skin contact causes ulcers and dermatitis. Wash hands before lunch and at the end of the day.',
      "Spill kit accessible — don't put fuel/oil drums on bare ground.",
    ].join('\n'),
    duration_mins: 8,
  },
  {
    id: 'mental_health',
    topic: 'Mental health and wellbeing',
    why: 'Construction has one of the highest suicide rates of any UK industry. Talking about it is the first step. Mates in Mind / Andy\'s Man Club resources are on the cabin wall.',
    points: [
      "It's OK not to be OK. Tell a mate, supervisor, or call Andy\'s Man Club (Mondays 7pm).",
      'Watch for changes in colleagues — quieter than usual, missing days, drinking more, irritable.',
      'Long hours, money pressure, isolation are the big stressors in this industry — recognise them.',
      'Samaritans 116 123 — 24/7, free from any phone, won\'t show on bills.',
      'Talking to one mate today could save a life this year. Not dramatic — true.',
    ].join('\n'),
    duration_mins: 8,
  },
  {
    id: 'lone_working',
    topic: 'Lone working',
    why: 'Working alone — opening up early, locking up late, doing remedials on a finished plot — has its own risk profile. Anyone going lone needs a check-in plan.',
    points: [
      'Tell someone where you\'ll be, what you\'re doing, expected duration.',
      'Agree a check-in time. If they don\'t hear from you, they call.',
      'Phone charged, signal known on site, ICE (in case of emergency) contact saved.',
      'No hot works, working at height >2m, or excavation entry alone — full stop.',
      'First aid kit + water + warm jacket in the van for unexpected delays.',
    ].join('\n'),
    duration_mins: 6,
  },
  {
    id: 'weather_cold',
    topic: 'Working in cold / wet weather',
    why: 'Cold and wet conditions cause more falls, sprains and HAVS flare-ups. Hypothermia is real after a few hours soaked through in single digits.',
    points: [
      'Layered clothing — base layer + insulated mid + waterproof shell. Keep a dry change of base layer in the van.',
      'Hot drinks every 60-90 mins; eat regularly to keep core warm.',
      'Watch grip on scaffolds, ladders, walking surfaces — extra care or stop.',
      'HAVS symptoms worsen in the cold — keep hands warm between tool uses.',
      'If forecast brings ice / snow / wind >40mph, review whether work continues at height.',
    ].join('\n'),
    duration_mins: 6,
  },
  {
    id: 'weather_hot',
    topic: 'Working in hot weather',
    why: 'UK summers are hitting 30C+ regularly now. Heat exhaustion and skin cancer from UV are both rising in the trades. Easy to manage with a few habits.',
    points: [
      'Sunscreen SPF30+ on neck, ears, arms, hands — reapply every 2 hours, especially if sweating.',
      'Long-sleeved hi-vis or UV-blocking sleeves — hard hat with neck flap if outside all day.',
      'Water — at least 250ml per hour. No dehydration as a "normal" state by 3pm.',
      'Schedule heavy / hot work for the cooler morning where possible.',
      'Heat exhaustion signs: dizzy, headache, nausea, cramps. Sit down in shade, water, tell someone.',
    ].join('\n'),
    duration_mins: 6,
  },
  {
    id: 'first_aid_awareness',
    topic: 'First aid awareness',
    why: 'Everyone should know who the appointed first aider is, where the kit lives, and the address to give the ambulance. Refresh every quarter.',
    points: [
      'Site first aiders today: [name(s)]. Reach them by [radio / phone / shout].',
      'First aid kit is in the welfare cabin. Eyewash next to it. Burns dressing in the box.',
      'Defib — nearest one is at [location] OR no on-site defib (call 999, they will direct).',
      'Site address for the ambulance: [postcode]. Saved as the site induction info too.',
      'Call 999 first for anything more than a small cut or bruise. Tell the Director.',
    ].join('\n'),
    duration_mins: 5,
  },
  {
    id: 'spill_response',
    topic: 'Spill response',
    why: 'Fuel and oil from plant, chemicals from materials — every spill is an environmental incident if untreated, and a slip hazard until it\'s cleaned.',
    points: [
      'Spill kit location: [welfare cabin / yard]. Check it\'s stocked weekly.',
      'Contain first — boom or absorbent socks around the spill before it spreads.',
      'Soak it up with absorbent pads / granules. Don\'t hose into a drain — drains lead to watercourses.',
      'Used absorbent goes in a hazardous-waste bag, sealed, stored, disposed via licensed carrier.',
      'Report any spill >5 litres to the Director immediately — Environment Agency may need to know.',
    ].join('\n'),
    duration_mins: 7,
  },
];
